import { useLayoutEffect, useRef, useState } from "react";
import type { ImportPreviewOrg } from "../../utils/bpaImport";
import { remainingMoveMs, SETTLE_MARGIN_MS } from "./motionTiming";

type PanelView = {
  open: boolean;
  lastOrg: ImportPreviewOrg | null;
  moving: boolean;
  move: number;
};

/**
 * Drives the compare panel's open and close move. `org` is the organisation being compared, or
 * null when none is. The panel opens or closes at once; it only moves when the page called
 * `expectMove()` in the same render that `open` changed, which is how the editor's own open
 * (Compare) and close (✕, Escape) are told apart from the list reloading or a new source being
 * picked. Those go straight to their end state and an unexpected close unmounts at once.
 *
 * The expectation is state, so it renders even when the compared key is set to the same value, and
 * it lasts one render: if `open` did not change in that render it is dropped. A move ends by a
 * timer taken from the shell's and its direct children's remaining transition time plus a margin,
 * so a transition that never reports an end cannot strand the panel part-way.
 *
 * `drawn` is the organisation to render: the current one, or the last one while the panel closes.
 */
export function useComparePanelMotion(org: ImportPreviewOrg | null) {
  const shellRef = useRef<HTMLDivElement>(null);
  const [expected, setExpected] = useState(false);
  const open = org !== null;
  const [view, setView] = useState<PanelView>({ open, lastOrg: org, moving: false, move: 0 });

  if (expected) setExpected(false);
  if (view.open !== open || (org !== null && view.lastOrg !== org)) {
    const startsMove = expected && view.open !== open;
    setView({
      open,
      lastOrg: org ?? view.lastOrg,
      moving: view.open === open ? view.moving : startsMove,
      move: startsMove ? view.move + 1 : view.move,
    });
  }

  const { moving, move } = view;

  useLayoutEffect(() => {
    if (!moving) return;
    const finish = () =>
      setView(current => (current.move === move ? { ...current, moving: false } : current));
    const remaining = shellRef.current ? remainingMoveMs(shellRef.current) : 0;
    if (remaining <= 0) {
      finish();
      return;
    }
    const timer = window.setTimeout(finish, remaining + SETTLE_MARGIN_MS);
    return () => window.clearTimeout(timer);
  }, [moving, move]);

  return {
    drawn: org ?? (moving ? view.lastOrg : null),
    open,
    moving,
    shellRef,
    expectMove: () => setExpected(true),
  };
}
